import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import { FaPlus, FaArrowLeft, FaTrash, FaEdit } from "react-icons/fa";
import ConfirmationModal from "../components/ConfirmationModal";
import Switch from "../components/Switch";

// Componente para a lista de usuários
const UserList = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [modalState, setModalState] = useState({ isOpen: false, userId: null });
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);

  const fetchUsuarios = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsuarios(data);
    } catch (error) {
      addToast("Erro ao buscar usuários.", "error");
    }
  }, [addToast]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const handleDelete = (id) => {
    setModalState({ isOpen: true, userId: id });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${modalState.userId}`);
      addToast("Usuário excluído com sucesso!", "success");
      fetchUsuarios();
    } catch (error) {
      addToast("Erro ao excluir usuário.", "error");
    } finally {
      setModalState({ isOpen: false, userId: null });
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, userId: null })}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este usuário?"
      />
      <PageHeader title="Usuários">
        <button onClick={() => navigate("/admin/usuarios/novo")}> 
          <FaPlus />
          <span>Criar Novo Usuário</span>
        </button>
      </PageHeader>
      <Card>
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF</th>
              <th>Email</th>
              <th>Perfil</th>
              <th className="column-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.length > 0 ? (
              usuarios.map((user) => (
                <tr key={user.id}>
                  <td data-label="Nome">
                    {user.nome} {user.sobrenome}
                  </td>
                  <td data-label="CPF">{user.cpf}</td>
                  <td data-label="Email">{user.email}</td>
                  <td data-label="Perfil">
                    {user.perfil_id === 1
                      ? "Admin"
                      : user.perfil_id === 2
                      ? "Controlador"
                      : "Usuário"}
                  </td>
                  <td
                    data-label="Ações"
                    className="actions-cell column-actions"
                  >
                    <Link
                      to={`/admin/usuarios/editar/${user.id}`}
                      className="action-btn edit"
                    >
                      <FaEdit />
                    </Link>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="action-btn delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="muted-text">
                  Nenhum usuário cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
};

// Componente para o formulário de criação/edição
const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const [formData, setFormData] = useState({
    nome: "",
    sobrenome: "",
    cpf: "",
    email: "",
    senha: "",
    perfil_id: "3",
    email_verificado: false,
  });
  const isEditing = !!id;

  useEffect(() => {
    if (isEditing) {
      api.get(`/users/${id}`).then((response) => {
        setFormData({ ...response.data, senha: "" });
      });
    }
  }, [id, isEditing]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await api.put(`/users/${id}`, formData);
        addToast("Usuário atualizado com sucesso!", "success");
      } else {
        await api.post("/users/admin-create", formData);
        addToast("Usuário criado com sucesso!", "success");
      }
      navigate("/admin/usuarios");
    } catch (error) {
      addToast(
        error.response?.data?.message || "Erro ao salvar usuário.",
        "error"
      );
    }
  };

  return (
    <>
      <PageHeader title={isEditing ? "Editar Usuário" : "Criar Novo Usuário"}>
        <button
          onClick={() => navigate("/admin/usuarios")}
          className="button-secondary"
        >
          <FaArrowLeft />
          <span>Voltar para a Lista</span>
        </button>
      </PageHeader>
      <Card>
        <form onSubmit={handleSubmit} className="form-modern form-grid">
          <div className="form-group">
            <label htmlFor="nome">Nome</label>
            <input
              id="nome"
              name="nome"
              value={formData.nome || ""}
              onChange={handleInputChange}
              placeholder="Nome"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="sobrenome">Sobrenome</label>
            <input
              id="sobrenome"
              name="sobrenome"
              value={formData.sobrenome || ""}
              onChange={handleInputChange}
              placeholder="Sobrenome"
              required
            />
          </div>
          <div className="form-group full-width">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              placeholder="Email"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="cpf">CPF</label>
            <input
              id="cpf"
              name="cpf"
              value={formData.cpf || ""}
              onChange={handleInputChange}
              placeholder="CPF"
              required
              disabled={isEditing}
            />
          </div>
          <div className="form-group">
            <label htmlFor="perfil_id">Perfil</label>
            <select
              id="perfil_id"
              name="perfil_id"
              value={formData.perfil_id}
              onChange={handleInputChange}
              required
            >
              <option value="1">ADMIN</option>
              <option value="2">CONTROLADOR</option>
              <option value="3">USUARIO</option>
            </select>
          </div>
          {isEditing && (
            <div className="form-group">
              <Switch
                id="email_verificado"
                name="email_verificado"
                label="Email Verificado"
                checked={formData.email_verificado}
                onChange={handleInputChange}
              />
            </div>
          )}
          <div className="form-group full-width">
            <label htmlFor="senha">Senha</label>
            <input
              id="senha"
              name="senha"
              type="password"
              value={formData.senha || ""}
              onChange={handleInputChange}
              placeholder={
                isEditing
                  ? "Nova Senha (deixe em branco para não alterar)"
                  : "Senha"
              }
              required={!isEditing}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-modern">
              Salvar
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/usuarios")}
              className="button-secondary"
            >
              <span>Cancelar</span>
            </button>
          </div>
        </form>
      </Card>
    </>
  );
};

export { UserList, UserForm };
